import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface Company {
  id: string;
  name: string;
  hqCountryCode: string;
  countries: string[];
  fiscalYearStartMonth: number;
  reportingCurrency: string;
  primaryLocale: string;
  industry: string | null;
  plan: 'free' | 'growth' | 'global' | 'enterprise';
  verificationStatus: 'pending' | 'verified' | 'pending_review' | 'rejected';
  aiModelDefault: string;
  aiMonthlyCapCents: number | null;
  createdAt: string;
}

export interface CreateCompanyInput {
  name: string;
  hqCountryCode: string;
  countries: string[];
  fiscalYearStartMonth: number;
  reportingCurrency: string;
  primaryLocale: string;
  industry?: string;
  companyUrl?: string;
}

export async function fetchMyCompany(): Promise<Company | null> {
  try {
    const r = await api.get<Company>('/api/companies/me');
    return r.data;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 401 || status === 403 || status === 404) return null;
    throw err;
  }
}

export function useMyCompany() {
  return useQuery({
    queryKey: ['company', 'me'],
    queryFn: fetchMyCompany,
    retry: 0,
    staleTime: 30_000,
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCompanyInput) => {
      const r = await api.post<{ company: Company; verificationReason: string }>(
        '/api/companies',
        input,
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['company', 'me'] });
      qc.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });
}

/**
 * Partial workspace update. Currently only the AI model default
 * and monthly AI spend cap are mutable; renames and country-list
 * changes go through a different flow.
 */
export function useUpdateMyCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { aiMonthlyCapCents?: number | null; industry?: string }) => {
      const r = await api.patch<Company>('/api/companies/me', input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['company', 'me'] }),
  });
}

/**
 * Smart defaults per HQ country — JP starts April + 千円 + JPY, US starts Jan + USD.
 * Used to pre-fill the onboarding form.
 */
export function defaultsForCountry(country: string) {
  const c = country.toUpperCase();
  switch (c) {
    case 'JP':
      return {
        fiscalYearStartMonth: 4,
        reportingCurrency: 'JPY',
        primaryLocale: 'ja',
      };
    case 'US':
    case 'GB':
    case 'CA':
    case 'AU':
    case 'NZ':
      return { fiscalYearStartMonth: 1, reportingCurrency: c === 'GB' ? 'GBP' : c === 'CA' ? 'CAD' : c === 'AU' ? 'AUD' : c === 'NZ' ? 'NZD' : 'USD', primaryLocale: 'en' };
    case 'KR':
      return { fiscalYearStartMonth: 1, reportingCurrency: 'KRW', primaryLocale: 'ko' };
    case 'DE':
    case 'FR':
    case 'ES':
    case 'IT':
    case 'NL':
      return {
        fiscalYearStartMonth: 1,
        reportingCurrency: 'EUR',
        primaryLocale: c === 'DE' ? 'de' : c === 'FR' ? 'fr' : c === 'ES' ? 'es' : 'en',
      };
    default:
      return { fiscalYearStartMonth: 1, reportingCurrency: 'USD', primaryLocale: 'en' };
  }
}
